import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { BreadcrumbItem } from '../components/Header';
import { useDialog } from '../context/DialogContext';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { progressService } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import inventoryStyles from '../styles/inventoryModal.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { getStepActionSchema } from '../config/stepActionSchemas';

interface Step6Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

interface DefectAnswers {
  hurts: string;
  helps: string;
  less: string;
}

const Step6: React.FC<Step6Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { showConfirm, showAlert, showHelp } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Six",
      description: "Grasp what 'entire readiness' truly means-and how it prepares me deeply for change",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step Six (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Six",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Work through character defects",
      description: "Answer questions about each character defect to become ready to let them go",
      completed: false
    },
    {
      id: 4,
      title: "Share and review your work with your sponsor",
      description: "Review your Step Six work with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
      completed: false
    },
    {
      id: 5,
      title: "Take Step Six: Were entirely ready to have God remove all these defects of character",
      description: "Complete Step Six by acknowledging your readiness to let go of character defects",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  // Character defects state (separate from Step 5)
  const [characterDefects, setCharacterDefects] = useState<string[]>([]);
  const [defectAnswers, setDefectAnswers] = useState<{ [defect: string]: DefectAnswers }>({});
  const [expandedDefect, setExpandedDefect] = useState<string | null>(null);
  const [editingDefect, setEditingDefect] = useState<string | null>(null);
  const [currentForm, setCurrentForm] = useState<(DefectAnswers & { defectName?: string }) | null>(null);
  
  // Auto-save state management
  const [savingDefect, setSavingDefect] = useState<string | null>(null);
  const [savedDefect, setSavedDefect] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const periodicSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getShareContext = useCallback(
    () => ({
      step6Defects: characterDefects,
      step6DefectAnswers: defectAnswers,
    }),
    [characterDefects, defectAnswers]
  );
  const { openShareDialog, shareDialogProps } = useStepShareDialog(6, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Six', path: '/saa/12-steps/step-6', active: true }
  ];

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Fetch Step 6 progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 6).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        
        // Restore actions state
        if (savedData.actions) {
          const merged = mergeStepActions(6, originalActions, savedData, !!existingProgress.completed);
          setActions(merged.actions);
          if (merged.migrationApplied) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 6,
              step_title: 'Step Six',
              completed: !!existingProgress.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
            });
          }
        }
        
        // Restore Step 6 character defects (stored separately from Step 5)
        if (savedData.step6_character_defects) {
          setCharacterDefects(savedData.step6_character_defects);
        }
        
        // Restore defect answers
        if (savedData.step6_defect_answers) {
          setDefectAnswers(savedData.step6_defect_answers);
        }
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err: any) {
      console.error('Error initializing progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user, stepWorkspaceRevision]);

  // Immediate save function (bypasses debounce)
  const immediateSaveDefectData = useCallback(async (defect: string, formData: DefectAnswers & { defectName?: string }): Promise<boolean> => {
    // Handle defect name change (rename)
    const newName = formData.defectName?.trim();
    const oldName = defect;
    let actualDefectName = oldName;
    let updatedDefects = [...characterDefects];
    let updatedAnswers = { ...defectAnswers };

    // If name changed, handle rename
    if (newName && newName !== oldName && oldName !== '__new__') {
      // Check if new name already exists
      if (characterDefects.includes(newName)) {
        showAlert({
          message: 'This character defect name already exists in your list.',
          variant: 'warning'
        });
        return false;
      }

      // Update defects array
      updatedDefects = characterDefects.map(d => d === oldName ? newName : d);
      setCharacterDefects(updatedDefects);

      // Update answers with new key
      updatedAnswers = {};
      Object.keys(defectAnswers).forEach(key => {
        if (key === oldName) {
          updatedAnswers[newName] = defectAnswers[key];
        } else {
          updatedAnswers[key] = defectAnswers[key];
        }
      });
      
      actualDefectName = newName;
    }

    // Prepare answers (without defectName field)
    const answers: DefectAnswers = {
      hurts: formData.hurts,
      helps: formData.helps,
      less: formData.less
    };

    // Check if answers changed
    const hasChanges = JSON.stringify(answers) !== JSON.stringify(defectAnswers[actualDefectName]) ||
                       (newName && newName !== oldName);
    if (!hasChanges) return true;

    try {
      updatedAnswers[actualDefectName] = answers;

      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 6,
        step_title: 'Step Six',
        completed: false,
        data: stepDataWithActions(actions, getStepActionSchema(6).actionSchemaVersion, {
          step6_character_defects: updatedDefects,
          step6_defect_answers: updatedAnswers
        })
      });

      setDefectAnswers(updatedAnswers);
      return true;
    } catch (error) {
      console.error('Error saving defect data:', error);
      return false;
    }
  }, [defectAnswers, characterDefects, actions, showAlert]);

  // Auto-save function with debouncing
  const autoSaveDefectData = useCallback(async (defect: string, formData: DefectAnswers & { defectName?: string }) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      // Prepare answers without defectName field
      const answers: DefectAnswers = {
        hurts: formData.hurts,
        helps: formData.helps,
        less: formData.less
      };

      const hasChanges = JSON.stringify(answers) !== JSON.stringify(defectAnswers[defect]) ||
                         (formData.defectName && formData.defectName.trim() !== defect);
      if (!hasChanges) return;

      // Set saving state
      setSavingDefect(defect);
      setSaveError(null);
      setSavedDefect(null);

      try {
        let finalDefectName = defect;
        let updatedDefects = [...characterDefects];
        let updatedAnswers = { ...defectAnswers };

        // Handle rename if name changed
        if (formData.defectName && formData.defectName.trim() !== defect) {
          const newName = formData.defectName.trim();
          
          // Check if new name already exists
          if (characterDefects.includes(newName) && newName !== defect) {
            setSavingDefect(null);
            setSaveError(defect);
            return;
          }

          // Update defects array
          updatedDefects = characterDefects.map(d => d === defect ? newName : d);
          setCharacterDefects(updatedDefects);

          // Update answers with new key
          updatedAnswers = {};
          Object.keys(defectAnswers).forEach(key => {
            if (key === defect) {
              updatedAnswers[newName] = answers;
      } else {
              updatedAnswers[key] = defectAnswers[key];
            }
          });
          
          finalDefectName = newName;
        } else {
          updatedAnswers[defect] = answers;
        }

        await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 6,
          step_title: 'Step Six',
          completed: false,
          data: stepDataWithActions(actions, getStepActionSchema(6).actionSchemaVersion, {
            step6_character_defects: updatedDefects,
            step6_defect_answers: updatedAnswers
          })
        });

        setDefectAnswers(updatedAnswers);

        // Show success feedback
        setSavingDefect(null);
        setSavedDefect(finalDefectName);
        setTimeout(() => setSavedDefect(null), 1000);
      } catch (error) {
        console.error('Error auto-saving defect data:', error);
        setSavingDefect(null);
        setSaveError(defect);
      }
    }, 300);
  }, [defectAnswers, characterDefects, actions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, []);

  // Periodic auto-save (every 10 seconds when defect is expanded)
  useEffect(() => {
    if (periodicSaveIntervalRef.current) {
      clearInterval(periodicSaveIntervalRef.current);
    }

    if (expandedDefect !== null && expandedDefect !== '__new__' && currentForm && user) {
      periodicSaveIntervalRef.current = setInterval(() => {
        autoSaveDefectData(expandedDefect, currentForm);
      }, 10000); // 10 seconds
    }

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, [expandedDefect, currentForm, user, autoSaveDefectData]);

  // Save on visibility change (when user switches browser tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && expandedDefect !== null && currentForm) {
        if (expandedDefect === '__new__') {
          handleSaveNewDefect();
        } else {
          autoSaveDefectData(expandedDefect, currentForm);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expandedDefect, currentForm, autoSaveDefectData]);


  // Handle defect name change for existing defects
  const handleDefectNameChange = async (oldName: string, newName: string) => {
    if (!currentForm) return;
    
    // Update the form with the new name (for tracking purposes)
    setCurrentForm({ ...currentForm, defectName: newName });
    
    // If the name actually changed from the original, we need to update the defects array
    if (newName.trim() && newName !== oldName) {
      // We'll handle the actual rename on collapse/save to avoid constant re-renders
      // Just update the local form state for now
    }
  };

  // Handle new defect name change
  const handleNewDefectNameChange = (name: string) => {
    if (!currentForm) return;
    
    // Just update the form - don't add to list yet
    // The conversion will happen on blur/collapse via handleSaveNewDefect
    setCurrentForm({ ...currentForm, defectName: name });
  };

  // Handle defect expand/collapse
  const handleToggleDefect = async (defect: string) => {
    // If expanding a different defect, save the current one first
    if (expandedDefect !== null && expandedDefect !== defect && currentForm) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Handle saving new entry or existing entry
      if (expandedDefect === '__new__') {
        await handleSaveNewDefect();
      } else {
        await immediateSaveDefectData(expandedDefect, currentForm);
      }
    }

    // Toggle expansion
    if (expandedDefect === defect) {
      // Collapsing - save before collapsing
      if (currentForm) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        
        if (defect === '__new__') {
          // Save new defect
          await handleSaveNewDefect();
        } else {
          // Save existing defect
          const saved = await immediateSaveDefectData(defect, currentForm);
          if (!saved) {
            showAlert({
              message: 'Warning: Failed to save your changes. Please try editing again or check your connection.',
              variant: 'warning'
            });
          }
        }
      }
      setExpandedDefect(null);
      setEditingDefect(null);
      setCurrentForm(null);
    } else {
      // Expanding
      if (defect === '__new__') {
        // Expanding new entry
        setExpandedDefect('__new__');
        setEditingDefect('__new__');
        setCurrentForm({ defectName: '', hurts: '', helps: '', less: '' });
      } else {
        // Expanding existing entry
        const existingAnswers = defectAnswers[defect] || { hurts: '', helps: '', less: '' };
        setExpandedDefect(defect);
        setEditingDefect(defect);
        setCurrentForm({ ...existingAnswers, defectName: defect });
      }
    }
  };

  // Handle saving new defect (called when collapsing __new__ entry or blur)
  const handleSaveNewDefect = async () => {
    // If no name was entered, just close without doing anything
    if (!currentForm || !currentForm.defectName || !currentForm.defectName.trim()) {
      return;
    }

    const defectName = currentForm.defectName.trim();
    
    // Check if defect already exists
    if (characterDefects.includes(defectName)) {
      // Already exists, just save any answer updates
      const answers: DefectAnswers = {
        hurts: currentForm.hurts,
        helps: currentForm.helps,
        less: currentForm.less
      };
      
      await immediateSaveDefectData(defectName, { ...answers, defectName });
      return;
    }

    // Add new defect to the list
    const newDefects = [...characterDefects, defectName];
    setCharacterDefects(newDefects);

    // Save answers
    const answers: DefectAnswers = {
      hurts: currentForm.hurts,
      helps: currentForm.helps,
      less: currentForm.less
    };
    
    const newAnswers = {
      ...defectAnswers,
      [defectName]: answers
    };
    setDefectAnswers(newAnswers);

    // Update expanded defect to the new name (so header shows correctly)
    setExpandedDefect(defectName);
    setEditingDefect(defectName);

    // Save to backend
    try {
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 6,
        step_title: 'Step Six',
        completed: false,
        data: stepDataWithActions(actions, getStepActionSchema(6).actionSchemaVersion, {
          step6_character_defects: newDefects,
          step6_defect_answers: newAnswers
        })
      });
      
    } catch (error) {
      console.error('Error saving new defect:', error);
    }
  };

  // Handle form field change
  const handleFormChange = (field: 'hurts' | 'helps' | 'less', value: string) => {
    if (!currentForm || !expandedDefect) return;
    
    const updatedForm = { ...currentForm, [field]: value };
    setCurrentForm(updatedForm);
    
    // Trigger auto-save (works for existing defects and newly created ones)
    // __new__ won't trigger auto-save until converted to a real defect name
    if (expandedDefect !== '__new__') {
      autoSaveDefectData(expandedDefect, updatedForm);
    }
  };

  // Handle delete defect
  const handleDeleteDefect = (defect: string) => {
    showConfirm({
      message: `Are you sure you want to delete '${defect}'? All answers for this character defect will be lost.`,
      onConfirm: async () => {
        // If deleting the currently expanded defect, clear the form
        if (expandedDefect === defect) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          setExpandedDefect(null);
          setEditingDefect(null);
          setCurrentForm(null);
        }

        const newDefects = characterDefects.filter(d => d !== defect);
        setCharacterDefects(newDefects);

        const newAnswers = { ...defectAnswers };
        delete newAnswers[defect];
        setDefectAnswers(newAnswers);

        // Save to API
        if (user) {
    try {
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 6,
        step_title: 'Step Six',
        completed: false,
              data: stepDataWithActions(actions, getStepActionSchema(6).actionSchemaVersion, {
                step6_character_defects: newDefects,
                step6_defect_answers: newAnswers
              })
            });
          } catch (error) {
            console.error('Error deleting defect:', error);
          }
        }
      },
      variant: 'danger'
    });
  };

  // Copy from Step 5
  const handleCopyFromStep5 = async () => {
    try {
      // Check if Step 6 already has defects
      if (characterDefects.length > 0) {
        showAlert({
          message: "You've already entered character defects in Step 6. To copy from Step 5, you'll need to delete your existing entries first.",
          variant: 'warning'
        });
        return;
      }

      // Load character defects from Step 5
      const step5Progress = await progressService.getStepProgress('SAA', '12-steps', 5);
      
      if (!step5Progress || !step5Progress.data || !step5Progress.data.characterDefects || step5Progress.data.characterDefects.length === 0) {
        showAlert({
          message: "No character defects found in Step 5. You can either complete Step 5 first, or enter your character defects directly here in Step 6.",
          variant: 'info'
        });
        return;
      }

      // Copy defects from Step 5
      const step5Defects = step5Progress.data.characterDefects;
      setCharacterDefects(step5Defects);

      // Initialize empty answers for all defects
      const initialAnswers: { [key: string]: DefectAnswers } = {};
      step5Defects.forEach((defect: string) => {
        initialAnswers[defect] = { hurts: '', helps: '', less: '' };
      });
      setDefectAnswers(initialAnswers);

      // Save to API
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 6,
        step_title: 'Step Six',
        completed: false,
        data: stepDataWithActions(actions, getStepActionSchema(6).actionSchemaVersion, {
          step6_character_defects: step5Defects,
          step6_defect_answers: initialAnswers
        })
      });

      showAlert({
        message: `Successfully copied ${step5Defects.length} character defect(s) from Step 5. Now answer the three questions for each one.`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Error copying from Step 5:', error);
      showAlert({
        message: 'Failed to copy from Step 5. Please try again.',
        variant: 'danger'
      });
    }
  };

  // Get completion count for a defect
  const getDefectCompletionCount = (defect: string): number => {
    const answers = defectAnswers[defect];
    if (!answers) return 0;
    
    let count = 0;
    if (answers.hurts.trim().length > 0) count++;
    if (answers.helps.trim().length > 0) count++;
    if (answers.less.trim().length > 0) count++;
    return count;
  };

  // Check if defect is complete
  const isDefectComplete = (defect: string): boolean => {
    return getDefectCompletionCount(defect) === 3;
  };

  // Validate action 3 completion
  const validateAction3Completion = () => {
    if (characterDefects.length === 0) return false;
    return characterDefects.every(defect => isDefectComplete(defect));
  };

  // Open help for questions
  const openHelpForQuestion = async (questionNum: number) => {
    try {
      const response = await fetch('/content/Step_6_Guidance.md');
      const text = await response.text();
      
      const startTag = `<<step6-question-${questionNum}-help start>>`;
      const endTag = `<<step6-question-${questionNum}-help end>>`;
      
      const startIndex = text.indexOf(startTag);
      const endIndex = text.indexOf(endTag);
      
      if (startIndex !== -1 && endIndex !== -1) {
        const helpContent = text.substring(startIndex + startTag.length, endIndex).trim();
        const title = questionNum === 0 ? 'Character Defects / Traits' : 'Help';
        showHelp({
          title: title,
          content: helpContent
        });
      } else {
        showHelp({
          title: 'Help',
          content: 'Help content not found for this question.'
        });
      }
    } catch (error) {
      console.error('Error loading help content:', error);
      showHelp({
        title: 'Help',
        content: 'Error loading help content.'
      });
    }
  };

  // Custom render function for action 3
  const renderAction3Content = (action: ActionItem, index: number) => {
    if (action.id !== 3) return null;

    const completedCount = characterDefects.filter(d => isDefectComplete(d)).length;

    return (
      <div className={styles.actionContentIndent}>
      <div style={{ marginBottom: 16, width: '100%' }}>
        {/* Copy from Step 5 button - styled like Watch Video button, right-aligned */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleCopyFromStep5}
            style={{
              background: 'white',
              color: '#1976d2',
              border: '2px solid #1976d2',
              borderRadius: 4,
              padding: '8px 16px',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: '14px'
            }}
            title="Copy character defects from Step 5"
          >
            Copy from Step Five
          </button>
        </div>

        {/* List of character defects - using inventory modal styles */}
        <div className={inventoryStyles.inventoryList}>
          {characterDefects.length > 0 && (
            <h4 className={inventoryStyles.inventoryTitle}>
              My Character Defects
              <span className={inventoryStyles.inventoryCount}>
                ({characterDefects.length} total, {completedCount} completed)
              </span>
            </h4>
          )}
          
          <ul className={inventoryStyles.inventoryItems}>
            {characterDefects.map((defect, idx) => {
              const isExpanded = expandedDefect === defect;
              const complete = isDefectComplete(defect);
              const currentAnswers = isExpanded && currentForm ? currentForm : (defectAnswers[defect] || { hurts: '', helps: '', less: '' });

            return (
                <li
                  key={defect}
                  className={`${inventoryStyles.inventoryItem} ${complete ? inventoryStyles.completeEntry : inventoryStyles.incompleteEntry}`}
                >
                  <div className={inventoryStyles.itemSummary} onClick={() => handleToggleDefect(defect)}>
                    <div className={inventoryStyles.itemText}>
                      <span className={inventoryStyles.expandIcon}>{isExpanded ? '▲' : '▼'}</span>
                      <span className={inventoryStyles.itemLabel}>{defect}</span>
                      {savingDefect === defect && (
                        <span className={inventoryStyles.savingText}>Saving...</span>
                      )}
                      {savedDefect === defect && (
                        <span className={inventoryStyles.savedText}>✓ Saved</span>
                      )}
                      {saveError === defect && (
                        <span className={inventoryStyles.saveErrorText}>⚠ Save failed</span>
                      )}
                      {savingDefect !== defect && savedDefect !== defect && saveError !== defect && (
                        <>
                          {complete && (
                            <span className={inventoryStyles.completeIcon}>✓</span>
                          )}
                          {!complete && (
                            <span className={inventoryStyles.incompleteText}>click to see/add details</span>
                          )}
                        </>
                      )}
                    </div>
                    <div className={inventoryStyles.itemControls}>
                      <button 
                        onClick={e => { e.stopPropagation(); handleDeleteDefect(defect); }} 
                        className={inventoryStyles.deleteButton}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {/* Edit Form (inline, shown directly when expanded) */}
                  {isExpanded && (
                    <div className={inventoryStyles.inventoryForm} data-defect={defect}>
                      {/* Character Defect Name Field */}
                      <div className={inventoryStyles.formGroup}>
                        <label>
                          Character Defect / Trait:
                          <button 
                            type="button" 
                            className={inventoryStyles.helpBtn} 
                            onClick={() => openHelpForQuestion(0)}
                            title="Get help with character defects"
                            tabIndex={-1}
                          >
                            ?
                          </button>
                        </label>
                        <input
                          type="text"
                          value={currentForm?.defectName || defect}
                          onChange={(e) => handleDefectNameChange(defect, e.target.value)}
                          placeholder="Enter character defect name..."
                        />
                      </div>

                      {/* Question 1 */}
                      <div className={inventoryStyles.formGroup}>
                        <label>
                    1. How does the trait hurt me?
                          <button 
                            type="button" 
                            className={inventoryStyles.helpBtn} 
                            onClick={() => openHelpForQuestion(1)}
                            title="Get help with this question"
                            tabIndex={-1}
                          >
                            ?
                          </button>
                  </label>
                  <textarea
                          value={currentAnswers.hurts}
                          onChange={(e) => handleFormChange('hurts', e.target.value)}
                          rows={3}
                          placeholder="Describe how this character defect negatively impacts your life..."
                  />
                </div>

                      {/* Question 2 */}
                      <div className={inventoryStyles.formGroup}>
                        <label>
                    2. How does the trait serve / help me?
                          <button 
                            type="button" 
                            className={inventoryStyles.helpBtn} 
                            onClick={() => openHelpForQuestion(2)}
                            title="Get help with this question"
                            tabIndex={-1}
                          >
                            ?
                          </button>
                  </label>
                  <textarea
                          value={currentAnswers.helps}
                          onChange={(e) => handleFormChange('helps', e.target.value)}
                          rows={3}
                          placeholder="Describe how this character defect might serve or protect you..."
                  />
                </div>

                      {/* Question 3 */}
                      <div className={inventoryStyles.formGroup}>
                        <label>
                          3. What would my life be like with less of this trait?
                          <button 
                            type="button" 
                            className={inventoryStyles.helpBtn} 
                            onClick={() => openHelpForQuestion(3)}
                            title="Get help with this question"
                            tabIndex={-1}
                          >
                            ?
                          </button>
                  </label>
                  <textarea
                          value={currentAnswers.less}
                          onChange={(e) => handleFormChange('less', e.target.value)}
                          rows={3}
                          placeholder="Imagine and describe how your life would be different with less of this trait..."
                  />
                </div>
                    </div>
                  )}
                </li>
              );
            })}

            {/* Add new defect card - white background to distinguish from incomplete entries */}
            {characterDefects.length < 9 && (
              <li className={inventoryStyles.inventoryItem} style={{ backgroundColor: 'white', borderColor: '#d1d5db' }}>
                <div className={inventoryStyles.itemSummary} onClick={() => handleToggleDefect('__new__')}>
                  <div className={inventoryStyles.itemText}>
                    <span className={inventoryStyles.expandIcon}>{expandedDefect === '__new__' ? '▲' : '▼'}</span>
                    <span className={inventoryStyles.itemLabel} style={{ fontStyle: 'italic', color: '#888' }}>
                      {characterDefects.length === 0 ? 'Add a character defect...' : 'Add another character defect...'}
                    </span>
                    {savingDefect === '__new__' && (
                      <span className={inventoryStyles.savingText}>Saving...</span>
                    )}
                    {savedDefect === '__new__' && (
                      <span className={inventoryStyles.savedText}>✓ Saved</span>
                    )}
                    {saveError === '__new__' && (
                      <span className={inventoryStyles.saveErrorText}>⚠ Save failed</span>
                    )}
                    {expandedDefect !== '__new__' && savingDefect !== '__new__' && savedDefect !== '__new__' && saveError !== '__new__' && (
                      <span className={inventoryStyles.incompleteText}>click to add</span>
                    )}
                  </div>
                </div>

                {/* Expanded content for new entry */}
                {expandedDefect === '__new__' && (
                  <div className={inventoryStyles.inventoryForm} data-defect="__new__">
                    {/* Character Defect Name Field */}
                    <div className={inventoryStyles.formGroup}>
                      <label>
                        Character Defect / Trait:
                  <button
                          type="button" 
                          className={inventoryStyles.helpBtn} 
                          onClick={() => openHelpForQuestion(0)}
                          title="Get help with character defects"
                          tabIndex={-1}
                        >
                          ?
                  </button>
                      </label>
                      <input
                        type="text"
                        value={currentForm?.defectName || ''}
                        onChange={(e) => handleNewDefectNameChange(e.target.value)}
                        onBlur={() => handleSaveNewDefect()}
                        placeholder="Enter character defect name..."
                        autoFocus
                      />
                </div>

                    {/* Question 1 */}
                    <div className={inventoryStyles.formGroup}>
                      <label>
                        1. How does the trait hurt me?
                        <button 
                          type="button" 
                          className={inventoryStyles.helpBtn} 
                          onClick={() => openHelpForQuestion(1)}
                          title="Get help with this question"
                          tabIndex={-1}
                        >
                          ?
                        </button>
                      </label>
                      <textarea
                        value={currentForm?.hurts || ''}
                        onChange={(e) => handleFormChange('hurts', e.target.value)}
                        rows={3}
                        placeholder="Describe how this character defect negatively impacts your life..."
                      />
              </div>

                    {/* Question 2 */}
                    <div className={inventoryStyles.formGroup}>
                      <label>
                        2. How does the trait serve / help me?
                        <button 
                          type="button" 
                          className={inventoryStyles.helpBtn} 
                          onClick={() => openHelpForQuestion(2)}
                          title="Get help with this question"
                          tabIndex={-1}
                        >
                          ?
                        </button>
                      </label>
                      <textarea
                        value={currentForm?.helps || ''}
                        onChange={(e) => handleFormChange('helps', e.target.value)}
                        rows={3}
                        placeholder="Describe how this character defect might serve or protect you..."
                      />
                    </div>

                    {/* Question 3 */}
                    <div className={inventoryStyles.formGroup}>
                      <label>
                        3. What would my life be like with less of this trait?
                        <button 
                          type="button" 
                          className={inventoryStyles.helpBtn} 
                          onClick={() => openHelpForQuestion(3)}
                          title="Get help with this question"
                          tabIndex={-1}
                        >
                          ?
                        </button>
                      </label>
                      <textarea
                        value={currentForm?.less || ''}
                        onChange={(e) => handleFormChange('less', e.target.value)}
                        rows={3}
                        placeholder="Imagine and describe how your life would be different with less of this trait..."
                      />
                    </div>
                  </div>
                )}
              </li>
            )}
          </ul>
        </div>
      </div>
      </div>
    );
  };

  // Check if any action can be completed
  const canCompleteAction = (actionId: number) => {
    if (actionId === 3) {
      return validateAction3Completion();
    }
    if (actionId === 4 || actionId === 5) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true;
  };

  // Handle action changes and save to backend
  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // Check if action 3 is being marked complete
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Action 3 is being marked complete - validate
      if (!validateAction3Completion()) {
        showAlert({
          message: 'Please answer all three questions for each character defect before marking this action complete.',
          variant: 'warning'
        });
        return;
      }
    }
    
      if (!user) {
        handleError(new Error('User not logged in'), 'Please log in to save your progress.');
        return;
      }
      
      try {
        // Update local state
        setActions(updatedActions);
        
        // Save to backend
        const allCompleted = updatedActions.every(action => action.completed);
        const updatedStepData = await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 6,
          step_title: 'Step Six',
          completed: allCompleted,
          data: stepDataWithActions(updatedActions, getStepActionSchema(6).actionSchemaVersion, {
            step6_character_defects: characterDefects,
            step6_defect_answers: defectAnswers
          })
        });
        
        // Update step data for history modal
        if (updatedStepData && updatedStepData.step) {
          setStepData(updatedStepData.step);
        }
        
        // Update status note
        const completedCount = updatedActions.filter(a => a.completed).length;
        await progressService.updateStatus({
          program: 'SAA',
          track: '12-steps',
          status_note: `Working Step Six - ${completedCount} of 5 actions completed`,
          progress_milestone: allCompleted ? 'step6_completed' : 'step6_in_progress'
        });
      } catch (err: any) {
        console.error('Error updating action progress:', err);
        handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Six</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={6}
                stepTitle="Step Six"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            "Were entirely ready to have God remove all these defects of character."
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!isInitializing && !errorMessage && (
          <>
        {!user && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && (
          <StepActions
            actions={withShareButton(6, actions, openShareDialog)}
            onActionChange={handleActionChange}
            renderActionContent={renderAction3Content}
            canCompleteAction={canCompleteAction}
            stepNumber={6}
          />
        )}

        {allCompleted && (
          <>
            <div className={styles.completionMessage}>
              <h3>🎉 Congratulations!</h3>
              <p>You have completed all actions for Step Six and can move on to Step Seven.</p>
            </div>
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-5" className={styles.prevStepLink}>
                  {'<<'} Step Five
                </a>
                <span></span>
                <a href="/saa/12-steps/step-7" className={styles.nextStepLink}>
                  Step Seven {'>>'}
                </a>
              </div>
            </div>
          </>
        )}

        {!allCompleted && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-5" className={styles.prevStepLink}>
                {'<<'} Step Five
              </a>
              <span></span>
              <a href="/saa/12-steps/step-7" className={styles.nextStepLink}>
                Step Seven {'>>'}
              </a>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-six start>>"
        endTag="<<step-six end>>"
      />

      {/* ContentReader for How to Work Step Six */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_6_Guidance.md"
        startTag="<<step6-guidance start>>"
        endTag="<<step6-guidance end>>"
        title="Guidance on working Step Six"
      />
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="Z-P1EZgxw4U"
        title="Step Six Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={6}
        stepTitle="Step Six"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />
      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step6; 
